/**
 * CodeMirrorEditor — CM6 による装飾付きエディタ（docs/03-editor.md §1, §5）。
 *
 * 位置づけ: **装飾（シンタックスハイライト）の上乗せのみ。** CM6 専用機能は作らない。
 * 入力補助は textarea と同じ `@shared/markdown-input` の純粋関数を通す。
 *
 * この実装が守っている IME 規律:
 * 1. **変換中は必ず沈黙する。** `view.composing`（CM6 内部の composition 判定）と、
 *    自前の `CompositionState`（compositionstart/end のみを正とする。ADR-022）の
 *    論理和で判定し、どちらかが立っていれば入力補助・DOM 書き換えを一切行わない。
 * 2. **リスト自動継続は keymap で実装しない。** CM6 は Enter を keymap で処理して
 *    keydown を preventDefault するため、`beforeinput` が届かない。そこで
 *    `EditorState.transactionFilter` で「改行 1 個の挿入トランザクション」を検知し、
 *    自前の編集へ差し替える（docs/03 §4-3 の「beforeinput 相当の検知」）。
 *    composition 由来のトランザクション（userEvent が `input.type.compose`）は素通しする。
 * 3. **毎更新に重い処理を走らせない。** `updateListener` は `docChanged` の判定と
 *    購読者への通知だけ。構文解析・保存判定などは一切ここに置かない。
 * 4. **Undo/Redo は CM6 の `history()` に任せる。** 独自実装しない（docs/03 §4-7）。
 * 5. React 側は props 変化で再描画しない（`memo` の比較関数が常に true）。
 *    差し替えは `key` による再マウントで行う。
 */

import { memo, useEffect, useRef } from 'react';
import {
  Annotation,
  EditorState,
  type Extension,
  type Transaction,
  type TransactionSpec,
} from '@codemirror/state';
import { EditorView, keymap, placeholder as placeholderExtension } from '@codemirror/view';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { markdown } from '@codemirror/lang-markdown';
import { syntaxHighlighting } from '@codemirror/language';
import { Strikethrough } from '@lezer/markdown';
import {
  continueListOnEnter,
  indentSelection as computeIndentSelection,
  type TextEdit,
  type TextState,
} from '@shared/markdown-input';
import {
  COMPOSITION_END_GRACE_MS,
  CompositionState,
  Subscribers,
  type EditorAdapter,
  type EditorViewProps,
} from './EditorAdapter';
import { markdownHighlightStyle } from './highlight';
import { refreshWysiwyg, wysiwygDecorations } from './wysiwyg';
import { createWithAndroidImeFallback } from './android-ime';
import './editor.css';

/** 自前で差し替えたトランザクションの目印。transactionFilter の再帰を防ぐ。 */
const listContinuation = Annotation.define<boolean>();

function readState(view: EditorView): TextState {
  const sel = view.state.selection.main;
  return {
    value: view.state.doc.toString(),
    selectionStart: sel.from,
    selectionEnd: sel.to,
  };
}

/**
 * 改行 1 個（＋自動インデントの空白のみ）を挿入するトランザクションかを判定する。
 * これが CM6 における「beforeinput の insertLineBreak / insertParagraph」相当。
 */
function isPlainNewlineInsertion(tr: Transaction): { pos: number } | null {
  let count = 0;
  let pos = -1;
  let ok = false;
  tr.changes.iterChanges((fromA, toA, _fromB, _toB, ins) => {
    count++;
    if (count > 1) return;
    const text = ins.toString();
    // 削除を伴わない挿入で、内容が「改行 ＋ 空白のみ」であること。
    ok = fromA === toA && text.startsWith('\n') && text.slice(1).trim() === '';
    pos = fromA;
  });
  return count === 1 && ok ? { pos } : null;
}

function createCodeMirrorAdapter(
  view: EditorView,
  composition: CompositionState,
  changeSubs: Subscribers,
  compositionEndSubs: Subscribers,
): EditorAdapter {
  // IME 規律: CM6 内部の composing フラグも見る。どちらかが true なら変換中。
  const isComposing = () => composition.value || view.composing;

  const applyEdit = (edit: TextEdit): void => {
    if (isComposing()) return;
    view.dispatch({
      changes: { from: edit.from, to: edit.to, insert: edit.insert },
      selection: { anchor: edit.selection.start, head: edit.selection.end },
      userEvent: 'input',
      scrollIntoView: true,
    });
  };

  return {
    getValue: () => view.state.doc.toString(),
    setValue: (value) => {
      // IME 規律: フォーカス中・変換中の全置換は禁止（docs/03 §4-6）。
      if (isComposing()) return;
      if (view.hasFocus) return;
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value },
      });
    },
    getSelection: () => {
      const sel = view.state.selection.main;
      return { start: sel.from, end: sel.to };
    },
    replaceRange: (from, to, text, newSelection) => {
      applyEdit({
        from,
        to,
        insert: text,
        selection: newSelection ?? { start: from + text.length, end: from + text.length },
      });
    },
    indentSelection: (dir) => {
      if (isComposing()) return;
      const edit = computeIndentSelection(readState(view), dir);
      if (edit) applyEdit(edit);
    },
    isComposing,
    onChange: (cb) => changeSubs.add(cb),
    onCompositionEnd: (cb) => compositionEndSubs.add(cb),
    focus: () => view.focus(),
    applyEdit,
  };
}

/** IME 規律: props 変化では再描画しない。 */
function neverRerender(): boolean {
  return true;
}

function CodeMirrorEditorImpl({
  initialValue,
  onReady,
  autoFocus,
  placeholder,
  className,
}: EditorViewProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const composition = new CompositionState();
    const changeSubs = new Subscribers();
    const compositionEndSubs = new Subscribers();
    // transactionFilter は state 構築時に必要だが view はまだ無いので、遅延参照する。
    const viewRef: { current: EditorView | null } = { current: null };
    let wysiwygRefreshTimer: ReturnType<typeof setTimeout> | null = null;

    const composing = () => composition.value || viewRef.current?.composing === true;
    const wysiwygFrozen = () =>
      composing() || composition.endedWithin(COMPOSITION_END_GRACE_MS);

    /**
     * リスト自動継続。CM6 では keydown が keymap に食われて beforeinput が来ないため、
     * トランザクション段階で「改行の挿入」を検知して差し替える。
     */
    const listContinuationFilter = EditorState.transactionFilter.of((tr) => {
      if (!tr.docChanged) return tr;
      if (tr.annotation(listContinuation)) return tr; // 自前の差し替えは素通し
      // IME 規律: 変換中・composition 由来の入力には一切介入しない。
      // 確定 Enter が compositionend の直後に届く揺れに備えて猶予窓も見る（ADR-022）。
      if (composing() || composition.endedWithin(COMPOSITION_END_GRACE_MS)) return tr;
      if (tr.isUserEvent('input.type.compose')) return tr;
      if (!tr.isUserEvent('input')) return tr;

      const hit = isPlainNewlineInsertion(tr);
      if (!hit) return tr;

      const sel = tr.startState.selection.main;
      // カーソル位置での単純な Enter のみを対象にする。
      if (!sel.empty || sel.from !== hit.pos) return tr;

      const edit = continueListOnEnter({
        value: tr.startState.doc.toString(),
        selectionStart: sel.from,
        selectionEnd: sel.to,
      });
      if (!edit) return tr; // 継続すべきでない → CM6 標準の改行に任せる

      const spec: TransactionSpec = {
        changes: { from: edit.from, to: edit.to, insert: edit.insert },
        selection: { anchor: edit.selection.start, head: edit.selection.end },
        annotations: listContinuation.of(true),
        userEvent: 'input',
        scrollIntoView: true,
      };
      return spec;
    });

    const domHandlers = EditorView.domEventHandlers({
      compositionstart: () => {
        if (wysiwygRefreshTimer !== null) clearTimeout(wysiwygRefreshTimer);
        wysiwygRefreshTimer = null;
        composition.start();
        return false;
      },
      compositionend: () => {
        composition.end();
        compositionEndSubs.emit();
        if (wysiwygRefreshTimer !== null) clearTimeout(wysiwygRefreshTimer);
        wysiwygRefreshTimer = setTimeout(() => {
          wysiwygRefreshTimer = null;
          viewRef.current?.dispatch({ effects: refreshWysiwyg.of(null) });
        }, COMPOSITION_END_GRACE_MS);
        return false;
      },
      keydown: (ev) => {
        // 229 は一時的ヒントとしてのみ記録する。変換中の判定には使わない（ADR-022）。
        composition.observeKeydown(ev.keyCode);
        return false;
      },
      blur: () => {
        composition.reset();
        return false;
      },
    });

    const extensions: Extension[] = [
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap]),
      // CM6 専用機能を作らない（docs/03 §1）ため、lang-markdown が既定で足す
      // 「CM6 独自の」振る舞いをすべて切る:
      // - addKeymap: 既定で Enter に insertNewlineContinueMarkup（CM6 版リスト継続）と
      //   Backspace に deleteMarkupBackward を割り当てる。これが有効だと textarea と
      //   挙動が分岐し、さらに我々の transactionFilter と二重にリスト継続してしまう。
      //   リスト継続は markdown-input.ts の共通ロジックだけに任せる。
      // - pasteURLAsLink: URL 貼り付けでリンク化する CM6 独自機能。textarea にはない。
      // - completeHTMLTags: 補完候補は IME の候補ウィンドウと干渉しうるので使わない。
      markdown({
        addKeymap: false,
        pasteURLAsLink: false,
        completeHTMLTags: false,
        extensions: Strikethrough,
      }),
      // 下線を使わない自前のハイライト（ADR-025）。defaultHighlightStyle は
      // 見出しとリンクに下線を当てるため、IME の未確定文字列の下線と衝突する。
      syntaxHighlighting(markdownHighlightStyle, { fallback: true }),
      EditorView.lineWrapping,
      listContinuationFilter,
      wysiwygDecorations(wysiwygFrozen),
      // 空のときだけ出る案内文（textarea の placeholder 属性と同じ役割）。
      // CM6 が既定エディタなので、これが無いと通常の利用でプレースホルダが出ない。
      // IME 規律: マウント時に組むだけで、以後この拡張は再構成しない。CM6 の
      // placeholder は本文とは別の装飾ウィジェットなので、composition には関与しない。
      ...(placeholder ? [placeholderExtension(placeholder)] : []),
      domHandlers,
      // IME 規律: ここで重い処理をしない。docChanged の判定と通知のみ。
      EditorView.updateListener.of((update) => {
        if (update.docChanged) changeSubs.emit();
      }),
      EditorView.theme({
        '&': { height: '100%' },
        '.cm-content': { fontFamily: 'inherit' },
      }),
    ];

    const createView = () =>
      new EditorView({
        state: EditorState.create({ doc: initialValue, extensions }),
        parent: host,
      });
    const view = createWithAndroidImeFallback(
      navigator.userAgent,
      EditorView as typeof EditorView & { EDIT_CONTEXT?: boolean },
      createView,
    );
    viewRef.current = view;

    const adapter = createCodeMirrorAdapter(view, composition, changeSubs, compositionEndSubs);

    if (autoFocus) {
      view.focus();
      const end = view.state.doc.length;
      view.dispatch({ selection: { anchor: end, head: end } });
    }

    onReadyRef.current(adapter);

    return () => {
      if (wysiwygRefreshTimer !== null) clearTimeout(wysiwygRefreshTimer);
      onReadyRef.current(null);
      changeSubs.clear();
      compositionEndSubs.clear();
      viewRef.current = null;
      view.destroy();
    };
    // 依存配列は空。再マウントは key で行う（IME 規律）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={hostRef} className={className ? `mm-editor-cm ${className}` : 'mm-editor-cm'} />;
}

/** IME 規律: props 変化では再描画しない。内容の差し替えは `key` による再マウントで。 */
export const CodeMirrorEditor = memo(CodeMirrorEditorImpl, neverRerender);
CodeMirrorEditor.displayName = 'CodeMirrorEditor';
